import { useCreateAgentExecutionConversationToken } from "@/queries/agent-queries";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/podkit/modal/Modal";
import { useEffect, useState } from "react";
import type { FC } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { Button } from "@/components/flexkit/Button";
import { IconDownload } from "@/assets/icons/geist/IconDownload";
import { IconCopy } from "@/assets/icons/geist/IconCopy";
import { useToast } from "@/components/podkit/toasts/use-toast";

export const ViewTranscriptModal: FC<{
    agentExecution: PlainAgentExecution;
    open: boolean;
    onClose: () => void;
}> = ({ agentExecution, open, onClose }) => {
    const [transcript, setTranscript] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fetchError, setFetchError] = useState<Error | undefined>(undefined);
    const { toast } = useToast();

    const { data: token, error: tokenError } = useCreateAgentExecutionConversationToken(agentExecution.id);

    useEffect(() => {
        const fetchTranscript = async () => {
            if (!open || !token || !agentExecution?.status?.transcriptUrl) {
                return;
            }

            setIsLoading(true);
            setFetchError(undefined);

            try {
                const response = await fetch(agentExecution.status.transcriptUrl, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch transcript: ${response.status} ${response.statusText}`);
                }

                const text = await response.text();
                setTranscript(text);
            } catch (err) {
                const error = err instanceof Error ? err : new Error(String(err));
                setFetchError(error);
            } finally {
                setIsLoading(false);
            }
        };

        void fetchTranscript();
    }, [open, token, agentExecution?.status?.transcriptUrl]);

    const handleCopyToClipboard = () => {
        void navigator.clipboard
            .writeText(transcript)
            .then(() => {
                toast({
                    title: "Copied to clipboard",
                    variant: "default",
                });
            })
            .catch((err) => {
                const error = err instanceof Error ? err : new Error(String(err));
                console.error("Failed to copy transcript:", error.message);
                toast({
                    title: "Failed to copy to clipboard",
                    variant: "default",
                });
            });
    };

    const handleDownload = () => {
        const blob = new Blob([transcript], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `transcript-${agentExecution.id}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const errorMessage = fetchError?.message || tokenError?.message || "Failed to load transcript";

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                className="flex max-h-[80vh] max-w-3xl flex-col"
                data-track-location="ona_view_transcript_modal"
            >
                <DialogHeader>
                    <DialogTitle>Agent Execution Transcript</DialogTitle>
                </DialogHeader>

                <div className="m-4 flex justify-end gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        LeadingIcon={IconCopy}
                        onClick={handleCopyToClipboard}
                        disabled={isLoading || !!fetchError}
                    >
                        Copy
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        LeadingIcon={IconDownload}
                        onClick={handleDownload}
                        disabled={isLoading || !!fetchError}
                    >
                        Download
                    </Button>
                </div>

                {isLoading ? (
                    <div>
                        <Text className="text-content-secondary">Loading transcript...</Text>
                    </div>
                ) : fetchError || tokenError ? (
                    <div>
                        <Text className="text-content-red">
                            Error: <span>{errorMessage}</span>
                        </Text>
                    </div>
                ) : transcript ? (
                    <div className="max-h-[60vh] w-full overflow-y-auto">
                        <pre className="w-full whitespace-pre-wrap break-normal rounded-md bg-surface-02 p-4 font-mono text-sm">
                            {transcript}
                        </pre>
                    </div>
                ) : (
                    <div>
                        <Text className="text-content-secondary">No transcript available</Text>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
